import {
  instanceOfPayrollRunContractorVendorBreakdown,
  PayrollRun,
  PayrollRunPayablesStatusesBreakdown
} from "@wingspanhq/wingspan-api-sdk";

import { parsePayrollAmount } from "../utils";

export type PayrollRunContractorAmounts = {
  payablesAmount: number;
  payablesCount: number;
  deductionsAmount: number;
  deductionsCount: number;
  /** Per-status payable counts; `undefined` until the run has been read with the breakdown expanded. */
  payablesStatuses: PayrollRunPayablesStatusesBreakdown | undefined;
  /** `undefined` until the run has been read with the breakdown expanded. */
  liabilityAmount: number | undefined;
};

/**
 * The contractor/vendor counterpart of `selectorPayrollRunBreakdownAmounts`:
 * reads the payables rollups off `PayrollRun.breakdown`.
 *
 * The API only populates `breakdown` when the read passes
 * `expand: [PayrollRunExpand.BREAKDOWN]`. `breakdown` is a union of the two
 * payroll shapes with no discriminator field, so it is narrowed by the SDK's
 * `instanceOf*` guard — an employee run yields zeros here.
 */
export const selectorPayrollRunContractorAmounts = (
  payrollRun: PayrollRun | undefined
): PayrollRunContractorAmounts => {
  const breakdown =
    payrollRun?.breakdown &&
    instanceOfPayrollRunContractorVendorBreakdown(payrollRun.breakdown)
      ? payrollRun.breakdown
      : undefined;

  // `payablesAmount` is what the run disburses; fall back to the run's own
  // `totalAmount` when the breakdown was not expanded.
  const liabilityAmount = breakdown?.payablesAmount ?? payrollRun?.totalAmount;

  return {
    payablesAmount: parsePayrollAmount(breakdown?.payablesAmount),
    payablesCount: breakdown?.payablesCount ?? 0,
    deductionsAmount: parsePayrollAmount(breakdown?.deductionsAmount),
    deductionsCount: breakdown?.deductionsCount ?? 0,
    payablesStatuses: breakdown?.payablesStatuses,
    liabilityAmount:
      liabilityAmount === undefined
        ? undefined
        : parsePayrollAmount(liabilityAmount)
  };
};
